import React, { useState, useEffect, useRef } from 'react';
import { X, CreditCard, Shield, CheckCircle, Loader2, IndianRupee, Tag, CheckCircle2, XCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROCESSING_MESSAGES = [
  "Securing your legacy...",
  "Connecting the dots...",
  "Let's go!"
];

const PaymentModal = ({ isOpen, onClose, onSuccess, subdomain, initialPrice, willId }) => {
  const [loading, setLoading] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [processingMessageIndex, setProcessingMessageIndex] = useState(0);
  const messageIntervalRef = useRef(null);
  
  // Coupon state
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(false);
  const [couponValidating, setCouponValidating] = useState(false);
  const [couponError, setCouponError] = useState('');
  const [discountAmount, setDiscountAmount] = useState(0);
  const [originalPrice, setOriginalPrice] = useState(initialPrice || 0);

  // Rotate processing messages every 2 seconds
  useEffect(() => {
    if (processingPayment) {
      setProcessingMessageIndex(0);
      messageIntervalRef.current = setInterval(() => {
        setProcessingMessageIndex(prev => (prev + 1) % PROCESSING_MESSAGES.length);
      }, 2000);
    } else {
      if (messageIntervalRef.current) {
        clearInterval(messageIntervalRef.current);
        messageIntervalRef.current = null;
      }
    }
    return () => {
      if (messageIntervalRef.current) {
        clearInterval(messageIntervalRef.current);
      }
    };
  }, [processingPayment]);

  const getAuthHeader = () => {
    const token = localStorage.getItem('clientToken');
    if (!token) {
      console.error('No client token found in localStorage');
    }
    return { Authorization: `Bearer ${token}` };
  };

  useEffect(() => {
    if (isOpen) {
      // Reset state when modal opens
      setCouponCode('');
      setCouponApplied(false);
      setCouponError('');
      setDiscountAmount(0);
      setProcessingPayment(false);
      
      // Use initialPrice if provided, otherwise fetch
      if (initialPrice != null) {
        setOriginalPrice(initialPrice);
      } else {
        fetchPrice();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialPrice]);

  const fetchPrice = async () => {
    try {
      const response = await axios.get(`${API}/client/partner-info`, {
        headers: getAuthHeader()
      });
      const willPrice = response.data.will_price ?? 999;
      setOriginalPrice(willPrice);
    } catch (error) {
      console.error('Failed to fetch price:', error);
      // Fallback: try to get from validate-coupon endpoint with empty coupon
      try {
        const fallbackResponse = await axios.post(
          `${API}/payment/validate-coupon`,
          { coupon_code: '', payment_type: 'will_creation' },
          { headers: getAuthHeader() }
        );
        setOriginalPrice(fallbackResponse.data.original_price ?? 999);
      } catch (fallbackError) {
        setOriginalPrice(999); // Default fallback
      }
    }
  };

  const validateCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError('Please enter a coupon code');
      return;
    }

    setCouponValidating(true);
    setCouponError('');

    try {
      const response = await axios.post(
        `${API}/payment/validate-coupon`,
        { coupon_code: couponCode.trim(), payment_type: 'will_creation' },
        { headers: getAuthHeader() }
      );

      if (response.data.valid) {
        setCouponApplied(true);
        setDiscountAmount(response.data.discount_amount);
        setOriginalPrice(response.data.original_price);
        toast.success(response.data.message);
      } else {
        setCouponError(response.data.message || 'Invalid coupon code');
        setCouponApplied(false);
      }
    } catch (error) {
      console.error('Coupon validation failed:', error);
      setCouponError(error.response?.data?.detail || 'Failed to validate coupon');
      setCouponApplied(false);
    } finally {
      setCouponValidating(false);
    }
  };

  const removeCoupon = () => {
    setCouponCode('');
    setCouponApplied(false);
    setCouponError('');
    setDiscountAmount(0);
  };

  const handlePayment = async () => {
    const token = localStorage.getItem('clientToken');
    if (!token) {
      toast.error('Session expired. Please login again.');
      onClose();
      return;
    }

    setProcessingPayment(true);
    setLoading(true);

    try {
      // Determine endpoint based on whether we have an existing will
      const endpoint = willId 
        ? `${API}/payment/checkout-sdk/draft/initiate`  // Pay for existing draft
        : `${API}/payment/checkout-sdk/initiate`;       // Create new will with payment
      
      const payload = { 
        coupon_code: couponApplied ? couponCode.trim() : null,
        ...(willId && { will_id: willId })
      };
      
      const response = await axios.post(endpoint, payload, { headers: getAuthHeader() });

      const data = response.data;
      
      // Handle demo account (payment skipped)
      if (data.is_demo && data.demo_completed) {
        setProcessingPayment(false);
        toast.success('Demo account - will activated successfully!');
        if (onSuccess) {
          onSuccess(data);
        }
        onClose();
        return;
      }
      
      if (data.success && data.order_id) {
        // Open Razorpay Checkout popup
        openRazorpayCheckout(data);
      } else {
        toast.error('Failed to initiate payment. Please try again.');
        setProcessingPayment(false);
      }
    } catch (error) {
      console.error('Failed to initiate payment:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to initiate payment. Please try again.';
      toast.error(errorMessage);
      setProcessingPayment(false);
    } finally {
      setLoading(false);
    }
  };

  const openRazorpayCheckout = (orderData) => {
    // Check if Razorpay is loaded
    if (!window.Razorpay) {
      toast.error('Payment system not loaded. Please refresh the page.');
      setProcessingPayment(false);
      return;
    }

    const options = {
      key: orderData.key_id,
      amount: orderData.amount,
      currency: orderData.currency,
      name: 'WillKart.ai',
      description: 'Will Creation Service',
      order_id: orderData.order_id,
      prefill: {
        name: orderData.prefill?.name || '',
        email: orderData.prefill?.email || '',
        contact: orderData.prefill?.contact || ''
      },
      notes: orderData.notes || {},
      theme: {
        color: '#f59e0b' // Amber color to match the UI
      },
      handler: async function(response) {
        // Payment successful - verify on backend
        await verifyPayment(response);
      },
      modal: {
        ondismiss: function() {
          setProcessingPayment(false);
          toast.info('Payment cancelled');
        }
      }
    };

    const rzp = new window.Razorpay(options);
    
    rzp.on('payment.failed', function(response) {
      setProcessingPayment(false);
      const errorMsg = response.error?.description || 'Payment failed. Please try again.';
      toast.error(errorMsg);
      console.error('Payment failed:', response.error);
    });

    rzp.open();
  };

  const verifyPayment = async (razorpayResponse) => {
    try {
      const response = await axios.post(
        `${API}/payment/checkout-sdk/verify`,
        {
          razorpay_order_id: razorpayResponse.razorpay_order_id,
          razorpay_payment_id: razorpayResponse.razorpay_payment_id,
          razorpay_signature: razorpayResponse.razorpay_signature
        },
        { headers: getAuthHeader() }
      );

      if (response.data.success) {
        setProcessingPayment(false);
        toast.success('Payment successful! Your will is now active.');
        
        // Call onSuccess callback with will_id so frontend can navigate
        if (onSuccess) {
          onSuccess(response.data);
        }
        
        onClose();
      } else {
        throw new Error('Verification failed');
      }
    } catch (error) {
      console.error('Payment verification failed:', error);
      setProcessingPayment(false);
      toast.error('Payment verification failed. Please contact support if amount was deducted.');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-slate-900 rounded-2xl max-w-md w-full shadow-2xl border border-slate-800 overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="relative bg-gradient-to-r from-amber-500/20 to-orange-500/20 p-4 border-b border-slate-800 flex-shrink-0">
          <button
            onClick={onClose}
            className="absolute top-3 right-3 text-slate-400 hover:text-white transition-colors"
            disabled={processingPayment}
            data-testid="close-payment-modal"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-500/20 rounded-xl">
              <CreditCard className="w-6 h-6 text-amber-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Complete Payment</h2>
              <p className="text-slate-400 text-xs">Secure payment via Razorpay</p>
            </div>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="p-4 overflow-y-auto flex-1">
          {/* Price Display */}
          <div className="mb-4 p-3 bg-slate-800/50 rounded-xl border border-slate-700">
            <div className="flex items-center justify-between">
              <span className="text-slate-300 text-sm">Will Creation Service</span>
              <div className="text-right">
                {discountAmount > 0 && (
                  <span className="text-slate-500 line-through text-xs mr-2">
                    ₹{originalPrice?.toLocaleString('en-IN')}
                  </span>
                )}
                <span className="text-xl font-bold text-white flex items-center">
                  <IndianRupee className="w-4 h-4" />
                  {originalPrice > 0 
                    ? (originalPrice - discountAmount).toLocaleString('en-IN')
                    : 'Loading...'
                  }
                </span>
              </div>
            </div>
            <p className="text-slate-500 text-xs mt-1">* Price includes 18% GST</p>
          </div>

          {/* Coupon Section */}
          <div className="mb-4">
            <label className="text-slate-400 text-xs mb-1 block">Have a coupon code?</label>
            {!couponApplied ? (
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-500" />
                  <Input
                    type="text"
                    placeholder="Enter coupon code"
                    value={couponCode}
                    onChange={(e) => {
                      setCouponCode(e.target.value.toUpperCase());
                      setCouponError('');
                    }}
                    className="pl-8 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 h-9 text-sm"
                    data-testid="coupon-input"
                    disabled={processingPayment}
                  />
                </div>
                <Button
                  onClick={validateCoupon}
                  disabled={couponValidating || !couponCode.trim() || processingPayment}
                  variant="outline"
                  className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10 h-9 text-sm"
                  data-testid="apply-coupon-btn"
                >
                  {couponValidating ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    'Apply'
                  )}
                </Button>
              </div>
            ) : (
              <div className="flex items-center justify-between p-2 bg-emerald-500/10 rounded-lg border border-emerald-500/30">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                  <span className="text-emerald-400 font-medium text-sm">{couponCode}</span>
                  <span className="text-emerald-400/70 text-xs">(-₹{discountAmount})</span>
                </div>
                <button
                  onClick={removeCoupon}
                  className="text-slate-400 hover:text-red-400 transition-colors"
                  data-testid="remove-coupon-btn"
                  disabled={processingPayment}
                >
                  <XCircle className="w-4 h-4" />
                </button>
              </div>
            )}
            {couponError && (
              <p className="text-red-400 text-xs mt-1 flex items-center gap-1">
                <XCircle className="w-3 h-3" />
                {couponError}
              </p>
            )}
          </div>

          {/* Features - Compact Grid */}
          <div className="grid grid-cols-2 gap-2 mb-4">
            {[
              'Legally valid document',
              'Professional formatting',
              'PDF & DOCX download',
              'Lifetime edit access',
            ].map((feature, index) => (
              <div key={index} className="flex items-center gap-2">
                <CheckCircle className="w-3 h-3 text-emerald-400 flex-shrink-0" />
                <span className="text-slate-300 text-xs">{feature}</span>
              </div>
            ))}
          </div>

          {/* Combined Info Badges */}
          <div className="space-y-2 mb-4">
            <div className="flex items-center gap-2 p-2 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
              <Shield className="w-4 h-4 text-emerald-400 flex-shrink-0" />
              <span className="text-emerald-400 text-xs">Secure payment via Razorpay • Details auto-filled</span>
            </div>
          </div>

          {/* Pay Button */}
          <Button
            onClick={handlePayment}
            disabled={processingPayment || loading}
            className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold py-4 rounded-xl shadow-lg shadow-amber-500/25"
            data-testid="pay-now-btn"
          >
            {processingPayment ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {PROCESSING_MESSAGES[processingMessageIndex]}
              </>
            ) : (
              <>
                <CreditCard className="w-4 h-4 mr-2" />
                Pay Now
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;
